import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authService } from '../services/auth';
import { apiService } from '../services/api';
import { organizationService } from '../services/organization';
import { User } from '../types';
import {
  User as UserIcon,
  Mail,
  Phone,
  Calendar,
  Building2,
  Edit3,
  Save,
  X,
  AlertCircle,
  MapPin,
  Clock,
  Lock,
  Eye,
  EyeOff,
  Plus,
  Trash2,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { SkeletonCard, ButtonLoader } from '../components/LoadingStates';

interface EditProfileForm {
  firstName: string;
  middleName?: string;
  lastName: string;
  email: string;
  mobile: string;
  dob: string;
}

interface ResetPasswordForm {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { currentTheme } = useTheme();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [photoZoomOpen, setPhotoZoomOpen] = useState(false);

  // Fetch user profile details
  const { data: profileData, isLoading, error } = useQuery(
    ['profile', user?.id],
    () => authService.getProfile(),
    {
      enabled: !!user,
    }
  );

  // Fetch organization details
  const { data: organizationData, isLoading: organizationLoading } = useQuery(
    ['organization', profileData?.organizationCode],
    () => organizationService.getOrganizationByCode(profileData?.organizationCode!),
    {
      enabled: !!profileData?.organizationCode,
    }
  );

  // Form handling
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<EditProfileForm>({
    defaultValues: profileData ? {
      firstName: profileData.firstName,
      middleName: profileData.middleName || '',
      lastName: profileData.lastName,
      email: profileData.email,
      mobile: profileData.mobile,
      dob: profileData.dob,
    } : {}
  });

  // Reset password form handling
  const {
    register: registerResetPassword,
    handleSubmit: handleSubmitResetPassword,
    reset: resetResetPasswordForm,
    watch,
    formState: { errors: resetPasswordErrors, isSubmitting: isResetPasswordSubmitting }
  } = useForm<ResetPasswordForm>();

  // Update profile mutation
  const updateProfileMutation = useMutation(
    (data: Partial<User>) => authService.updateProfile(data),
    {
      onSuccess: (updatedUser) => {
        queryClient.setQueryData(['profile', user?.id], updatedUser);
        queryClient.invalidateQueries(['profile']);
        setIsEditing(false);
        toast.success('Profile updated successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update profile');
      }
    }
  );

  // Reset password mutation
  const resetPasswordMutation = useMutation(
    (data: { oldPassword: string; newPassword: string }) => authService.resetPassword(data),
    {
      onSuccess: () => {
        setShowResetPasswordModal(false);
        resetResetPasswordForm();
        setShowOldPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        toast.success('Password reset successfully!');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to reset password');
      }
    }
  );

  // Photo upload mutation
  const uploadPhotoMutation = useMutation(
    (photo: string) => authService.updateProfile({ photo }),
    {
      onSuccess: (updatedUser) => {
        queryClient.setQueryData(['profile', user?.id], updatedUser);
        updateUser({ photo: updatedUser.photo });
        toast.success('Profile photo updated!');
      },
      onError: () => {
        toast.error('Failed to update photo');
      },
    }
  );

  const removePhotoMutation = useMutation(
    () => apiService.delete('/users/me/photo'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['profile', user?.id]);
        updateUser({ photo: undefined });
        toast.success('Profile photo removed');
      },
      onError: () => {
        toast.error('Failed to remove photo');
      },
    }
  );

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      uploadPhotoMutation.mutate(reader.result as string);
    };
    reader.readAsDataURL(file);
    // reset so same file can be re-selected
    e.target.value = '';
  };

  // Handle edit mode
  const handleEditClick = () => {
    if (profileData) {
      reset({
        firstName: profileData.firstName,
        middleName: profileData.middleName || '',
        lastName: profileData.lastName,
        email: profileData.email,
        mobile: profileData.mobile,
        dob: profileData.dob,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset();
  };

  const onSubmit = (data: EditProfileForm) => {
    updateProfileMutation.mutate({
      firstName: data.firstName,
      middleName: data.middleName || undefined,
      lastName: data.lastName,
      email: data.email,
      mobile: data.mobile,
      dob: data.dob,
    });
  };

  const onSubmitResetPassword = (data: ResetPasswordForm) => {
    resetPasswordMutation.mutate({
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
    });
  };

  if (isLoading || organizationLoading) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="space-y-4 sm:space-y-6">
          {/* Header Skeleton */}
          <SkeletonCard />

          {/* Profile Cards Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2">
              <SkeletonCard />
            </div>
            <div>
              <SkeletonCard />
            </div>
          </div>

          {/* Organization Card Skeleton */}
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="rounded-lg sm:rounded-xl border p-4 sm:p-6 text-center" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: '#fca5a5'
        }}>
          <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-3 sm:mb-4 text-red-500" />
          <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>
            Error Loading Profile
          </h3>
          <p className="text-sm sm:text-base" style={{ color: currentTheme.colors.text.secondary }}>
            Unable to load your profile information. Please try again.
          </p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="p-3 sm:p-4 md:p-6">
        <div className="rounded-lg sm:rounded-xl border p-4 sm:p-6 text-center" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          <UserIcon className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-3 sm:mb-4" style={{ color: currentTheme.colors.text.secondary }} />
          <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>
            No Profile Data
          </h3>
          <p className="text-sm sm:text-base" style={{ color: currentTheme.colors.text.secondary }}>
            Profile information is not available.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
            <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
            My Profile
          </h1>
          <p className="text-xs sm:text-sm mt-1" style={{ color: currentTheme.colors.text.secondary }}>
            View and manage your account information
          </p>
        </div>
        {!isEditing && (
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={() => setShowResetPasswordModal(true)}
              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-lg transition-colors border text-sm sm:text-base"
              style={{
                borderColor: currentTheme.colors.border.primary,
                color: currentTheme.colors.text.primary,
                backgroundColor: currentTheme.colors.background.secondary
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
            >
              <Lock className="h-4 w-4 mr-2" />
              Reset Password
            </button>
            <button
              onClick={handleEditClick}
              className="inline-flex items-center justify-center px-3 sm:px-4 py-2 text-white rounded-lg transition-colors text-sm sm:text-base"
              style={{ backgroundColor: currentTheme.colors.primary[600] }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              Edit Profile
            </button>
          </div>
        )}
      </div>

      {/* Profile Content */}
      <div className="max-w-4xl">
        <div className="rounded-lg sm:rounded-xl border shadow-sm" style={{
          backgroundColor: currentTheme.colors.background.card,
          borderColor: currentTheme.colors.border.primary
        }}>
          {isEditing ? (
            /* Edit Mode */
            <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
                <h2 className="text-lg sm:text-xl font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                  Edit Profile Information
                </h2>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-3 sm:px-4 py-2 border rounded-lg transition-colors text-sm sm:text-base inline-flex items-center justify-center"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      color: currentTheme.colors.text.primary,
                      backgroundColor: currentTheme.colors.background.secondary
                    }}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || updateProfileMutation.isLoading}
                    className="px-3 sm:px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center text-sm sm:text-base"
                    style={{ backgroundColor: '#22c55e' }}
                  >
                    {isSubmitting || updateProfileMutation.isLoading ? (
                      <ButtonLoader text="Saving..." />
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* First Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    {...register('firstName', { required: 'First name is required' })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.firstName ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  />
                  {errors.firstName && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.firstName.message}</p>
                  )}
                </div>

                {/* Middle Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Middle Name
                  </label>
                  <input
                    type="text"
                    {...register('middleName')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    {...register('lastName', { required: 'Last name is required' })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.lastName ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  />
                  {errors.lastName && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.lastName.message}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.email ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                {/* Mobile */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Mobile Number *
                  </label>
                  <input
                    type="tel"
                    {...register('mobile', {
                      required: 'Mobile number is required',
                      pattern: {
                        value: /^[6-9]\d{9}$/,
                        message: 'Enter a valid 10-digit mobile number'
                      }
                    })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.mobile ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                    maxLength={10}
                  />
                  {errors.mobile && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.mobile.message}</p>
                  )}
                </div>

                {/* Date of Birth */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    {...register('dob', { required: 'Date of birth is required' })}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 text-sm"
                    style={{
                      borderColor: errors.dob ? '#ef4444' : currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.secondary,
                      color: currentTheme.colors.text.primary
                    }}
                  />
                  {errors.dob && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.dob.message}</p>
                  )}
                </div>
              </div>
            </form>
          ) : (
            /* View Mode */
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center mb-4 sm:mb-6 gap-4">
                <div className="relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0">
                  <div
                    className="w-full h-full rounded-full flex items-center justify-center overflow-hidden"
                    style={{
                      backgroundColor: currentTheme.colors.primary[100],
                      cursor: profileData.photo ? 'zoom-in' : 'default',
                    }}
                    onClick={() => profileData.photo && setPhotoZoomOpen(true)}
                  >
                    {profileData.photo ? (
                      <img src={profileData.photo} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <UserIcon className="h-8 w-8 sm:h-10 sm:w-10" style={{ color: currentTheme.colors.primary[600] }} />
                    )}
                  </div>


                  {/* Upload (+) button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadPhotoMutation.isLoading}
                    className="absolute bottom-0 right-0 w-6 h-6 rounded-full flex items-center justify-center text-white shadow-md border-2 border-white disabled:opacity-60"
                    style={{ backgroundColor: currentTheme.colors.primary[600] }}
                    title="Upload profile photo"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl sm:text-2xl font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>
                    {`${profileData.firstName}${profileData.middleName ? ` ${profileData.middleName}` : ''} ${profileData.lastName}`}
                  </h2>
                  <p className="text-base sm:text-lg truncate" style={{ color: currentTheme.colors.text.secondary }}>
                    {organizationData?.name || profileData.organizationName || 'No Organization'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {/* Email */}
                <div className="flex items-center p-3 sm:p-4 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" style={{ color: currentTheme.colors.primary[600] }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium" style={{ color: currentTheme.colors.text.secondary }}>Email</p>
                    <p className="text-sm sm:text-base truncate" style={{ color: currentTheme.colors.text.primary }}>{profileData.email}</p>
                  </div>
                </div>

                {/* Mobile */}
                <div className="flex items-center p-3 sm:p-4 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                  <Phone className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" style={{ color: currentTheme.colors.primary[600] }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium" style={{ color: currentTheme.colors.text.secondary }}>Mobile</p>
                    <p className="text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>{profileData.mobile}</p>
                  </div>
                </div>

                {/* Date of Birth */}
                <div className="flex items-center p-3 sm:p-4 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0" style={{ color: currentTheme.colors.primary[600] }} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium" style={{ color: currentTheme.colors.text.secondary }}>Date of Birth</p>
                    <p className="text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                      {profileData.dob ? format(new Date(profileData.dob), 'MMMM dd, yyyy') : '—'}
                    </p>
                  </div>
                </div>

                {/* Organization */}
                <div className="md:col-span-2">
                  <div className="p-4 rounded-lg" style={{ backgroundColor: currentTheme.colors.background.secondary }}>
                    <div className="flex items-start">
                      <Building2 className="h-5 w-5 mr-3 mt-1" style={{ color: currentTheme.colors.primary[600] }} />
                      <div className="flex-1">
                        <p className="text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.secondary }}>Organization Details</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                              {organizationData?.name || profileData.organizationName || 'No Organization'}
                            </p>
                            <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                              Code: {organizationData?.code || profileData.organizationCode}
                            </p>
                            {organizationData?.address && (
                              <p className="text-sm flex items-start mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                                <MapPin className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                                {organizationData.address}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            {organizationData?.email && (
                              <p className="text-sm flex items-center" style={{ color: currentTheme.colors.text.secondary }}>
                                <Mail className="h-4 w-4 mr-2" />
                                {organizationData.email}
                              </p>
                            )}
                            {organizationData?.mobile && (
                              <p className="text-sm flex items-center" style={{ color: currentTheme.colors.text.secondary }}>
                                <Phone className="h-4 w-4 mr-2" />
                                {organizationData.mobile}
                              </p>
                            )}
                            {organizationData?.createdAt && (
                              <p className="text-sm flex items-center" style={{ color: currentTheme.colors.text.secondary }}>
                                <Clock className="h-4 w-4 mr-2" />
                                Since {format(new Date(organizationData.createdAt), 'MMM yyyy')}
                              </p>
                            )}
                            {organizationData && (
                              <div className="flex items-center">
                                <div
                                  className={`w-2 h-2 rounded-full mr-2 ${organizationData.active ? 'bg-green-500' : 'bg-red-500'}`}
                                ></div>
                                <span className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                                  {organizationData.active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Zoom Modal */}
      {photoZoomOpen && profileData?.photo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
          onClick={() => setPhotoZoomOpen(false)}
        >
          <div className="relative max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
            {/* Top bar: close + delete */}
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => {
                  removePhotoMutation.mutate();
                  setPhotoZoomOpen(false);
                }}
                disabled={removePhotoMutation.isLoading}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                {removePhotoMutation.isLoading ? 'Removing...' : 'Remove Photo'}
              </button>
              <button
                onClick={() => setPhotoZoomOpen(false)}
                className="text-white hover:text-gray-300 transition-colors"
              >
                <X className="h-8 w-8" />
              </button>
            </div>
            <img
              src={profileData.photo}
              alt="Profile"
              className="w-full rounded-2xl shadow-2xl object-contain max-h-[80vh]"
            />
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
          <div className="rounded-lg sm:rounded-xl max-w-md w-full" style={{
            backgroundColor: currentTheme.colors.background.card
          }}>
            <div className="p-4 sm:p-6 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
              <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
                  Reset Password
                </h2>
                <button
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    resetResetPasswordForm();
                    setShowOldPassword(false);
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  className="p-1.5 sm:p-2 rounded-lg transition-colors"
                  style={{ backgroundColor: currentTheme.colors.background.secondary }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" style={{ color: currentTheme.colors.text.primary }} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmitResetPassword(onSubmitResetPassword)} className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {/* Old Password */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Old Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showOldPassword ? 'text' : 'password'}
                      {...registerResetPassword('oldPassword', {
                        required: 'Old password is required'
                      })}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 pr-10 sm:pr-12 text-sm"
                      style={{
                        borderColor: resetPasswordErrors.oldPassword ? '#ef4444' : currentTheme.colors.border.primary,
                        backgroundColor: currentTheme.colors.background.secondary,
                        color: currentTheme.colors.text.primary
                      }}
                      placeholder="Enter your old password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                      style={{ color: currentTheme.colors.text.secondary }}
                    >
                      {showOldPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {resetPasswordErrors.oldPassword && (
                    <p className="text-red-500 text-sm mt-1">{resetPasswordErrors.oldPassword.message}</p>
                  )}
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      {...registerResetPassword('newPassword', {
                        required: 'New password is required',
                        minLength: {
                          value: 6,
                          message: 'Password must be at least 6 characters'
                        }
                      })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 pr-12"
                      style={{
                        borderColor: resetPasswordErrors.newPassword ? '#ef4444' : currentTheme.colors.border.primary,
                        backgroundColor: currentTheme.colors.background.secondary,
                        color: currentTheme.colors.text.primary
                      }}
                      placeholder="Enter your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                      style={{ color: currentTheme.colors.text.secondary }}
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {resetPasswordErrors.newPassword && (
                    <p className="text-red-500 text-sm mt-1">{resetPasswordErrors.newPassword.message}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: currentTheme.colors.text.primary }}>
                    Confirm New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...registerResetPassword('confirmPassword', {
                        required: 'Please confirm your new password',
                        validate: (value) => value === watch('newPassword') || 'Passwords do not match'
                      })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 pr-12"
                      style={{
                        borderColor: resetPasswordErrors.confirmPassword ? '#ef4444' : currentTheme.colors.border.primary,
                        backgroundColor: currentTheme.colors.background.secondary,
                        color: currentTheme.colors.text.primary
                      }}
                      placeholder="Confirm your new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                      style={{ color: currentTheme.colors.text.secondary }}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {resetPasswordErrors.confirmPassword && (
                    <p className="text-red-500 text-sm mt-1">{resetPasswordErrors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    resetResetPasswordForm();
                    setShowOldPassword(false);
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  }}
                  className="flex-1 px-4 py-3 rounded-lg transition-colors font-medium"
                  style={{
                    color: currentTheme.colors.text.primary,
                    backgroundColor: currentTheme.colors.background.secondary
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isResetPasswordSubmitting || resetPasswordMutation.isLoading}
                  className="flex-1 px-4 py-3 text-white rounded-lg transition-colors flex items-center justify-center font-medium disabled:opacity-50"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  onMouseEnter={(e) => !resetPasswordMutation.isLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                  onMouseLeave={(e) => !resetPasswordMutation.isLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                >
                  {isResetPasswordSubmitting || resetPasswordMutation.isLoading ? (
                    <ButtonLoader text="Resetting..." />
                  ) : (
                    <>
                      <Lock className="h-5 w-5 mr-2" />
                      Reset Password
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};